import React from 'react';
import AuthorityReviewFlag from './AuthorityReviewFlag';
import SCGCitation from './SCGCitation';

const StructuredClassificationDetails = ({ result }) => {
  const { details, raw_response } = result;

  // Function to automatically linkify SCG rule IDs in text
  const linkifyScgRules = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Regex to match SCG rule patterns: SCG-XXX, SCG-UNK-XXXXXX-XXX, etc.
    const scgRulePattern = /(SCG-[A-Z0-9-]+)/g;
    
    const parts = text.split(scgRulePattern);
    
    return parts.map((part, index) => {
      if (scgRulePattern.test(part)) {
        return (
          <SCGCitation 
            key={index}
            ruleId={part}
            compact={true}
          />
        );
      }
      return part;
    });
  };

  // If no structured details available, fall back to raw response with linkified SCG rules
  if (!details) {
    return (
      <div className="bg-gray-50 rounded-md p-3">
        <div className="text-sm text-gray-700 whitespace-pre-wrap">
          {linkifyScgRules(raw_response)}
        </div>
      </div>
    );
  }


  const SCGRuleLink = ({ citation }) => (
    <SCGCitation 
      ruleId={citation.rule_id}
      compact={true}
    />
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      {/* Authority Review Required Flag */}
      <AuthorityReviewFlag 
        reasoning={raw_response} 
        contradictions={result.contradictions}
        variant="detailed" 
        className="mb-4" 
      />
      
      {/* Contradictory Rules Alert */}
      {result.contradictions && result.contradictions.has_contradictions && (
        <div className={`border-l-4 p-4 rounded ${
          result.contradictions.authority_review_required 
            ? 'bg-red-50 border-red-500' 
            : 'bg-yellow-50 border-yellow-500'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {result.contradictions.authority_review_required ? (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium ${
                result.contradictions.authority_review_required 
                  ? 'text-red-800' 
                  : 'text-yellow-800'
              }`}>
                {result.contradictions.authority_review_required 
                  ? '⚠️ Authority Review Required - Contradictory SCG Rules Found' 
                  : '⚠️ Contradictory SCG Rules Detected'}
              </h3>
              <div className={`mt-2 text-sm ${
                result.contradictions.authority_review_required 
                  ? 'text-red-700' 
                  : 'text-yellow-700'
              }`}>
                <div className="space-y-2">
                  <div>
                    <strong>Conflicting Rules:</strong> {linkifyScgRules(result.contradictions.rule_conflicts)}
                  </div>
                  {result.contradictions.contradiction_details && (
                    <div>
                      <strong>Details:</strong> {linkifyScgRules(result.contradictions.contradiction_details)}
                    </div>
                  )}
                  {result.contradictions.resolution_reasoning && (
                    <div>
                      <strong>Resolution:</strong> {linkifyScgRules(result.contradictions.resolution_reasoning)}
                    </div>
                  )}
                  {result.contradictions.authority_review_required && (
                    <div className="mt-3 p-2 bg-red-100 rounded text-red-800 text-xs">
                      <strong>ACTION REQUIRED:</strong> These rule contradictions cannot be automatically resolved and require review by authoritative sources to update or clarify the conflicting SCG guidance.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary SCG Citation */}
      {details.primary_scg_citation && (
        <div className="border-l-4 border-blue-500 pl-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Primary SCG Rule Citation</h4>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <SCGRuleLink citation={details.primary_scg_citation} />
              <span className="text-sm text-gray-600">-</span>
              <span className="text-sm font-medium text-gray-700">"{details.primary_scg_citation.title}"</span>
            </div>
            <div className="text-xs text-gray-600">
              <span className="font-medium">Classification:</span> {details.primary_scg_citation.classification_level} | 
              <span className="font-medium ml-2">Reason:</span> {details.primary_scg_citation.reason_code}
            </div>
          </div>
        </div>
      )}

      {/* Secondary SCG Citations */}
      {details.secondary_scg_citations && details.secondary_scg_citations.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Secondary SCG Citations</h4>
          <div className="space-y-2">
            {details.secondary_scg_citations.map((citation, index) => (
              <div key={index} className="bg-gray-50 rounded p-2">
                <div className="flex items-start space-x-2">
                  <span className="text-gray-400 text-xs mt-1">•</span>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <SCGRuleLink citation={citation} />
                      <span className="text-sm text-gray-600">-</span>
                      <span className="text-sm text-gray-700">"{citation.title}"</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      {citation.classification_level} | {citation.reason_code}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rule Application Analysis */}
      {details.rule_application_analysis && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Rule Application Analysis</h4>
          <div className="bg-blue-50 rounded p-3">
            <div className="text-sm text-gray-700 leading-relaxed">
              {linkifyScgRules(details.rule_application_analysis)}
            </div>
          </div>
        </div>
      )}

      {/* Content Analysis */}
      {details.content_analysis && details.content_analysis.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Content Analysis</h4>
          <div className="bg-yellow-50 rounded p-3">
            <div className="space-y-2">
              {details.content_analysis.map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <span className="text-yellow-600 text-sm mt-1">{index + 1}.</span>
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Triggered Elements */}
      {details.triggered_elements && details.triggered_elements.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Triggered Elements</h4>
          <div className="flex flex-wrap gap-2">
            {details.triggered_elements.map((element, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800"
              >
                "{element}"
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Raw Response Fallback (collapsed by default) */}
      {raw_response && (
        <details className="mt-4">
          <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
            View Raw Analysis (Technical Details)
          </summary>
          <div className="mt-2 bg-gray-50 rounded p-3 border">
            <div className="text-xs text-gray-600 whitespace-pre-wrap font-mono overflow-x-auto">
              {linkifyScgRules(raw_response)}
            </div>
          </div>
        </details>
      )}
    </div>
  );
};

export default StructuredClassificationDetails;